import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useRide } from '../contexts/RideContext';
import { format, formatDistanceToNow } from 'date-fns';
import { MapPin, Package, Utensils, Briefcase, ArrowRight, ArrowLeft, Loader2, X, Star, MessageSquare, CheckCircle, Clock, Shield, Truck, Navigation, Utensils as UtensilsIcon, Briefcase as BriefcaseIcon } from 'lucide-react';

const statusLabels: Record<string, string> = {
  requested: 'Demandée',
  searching: 'Recherche livreur',
  assigned: 'Assignée',
  accepted: 'Acceptée',
  en_route_pickup: 'En route vers le pickup',
  picked_up: 'Récupéré',
  en_route_delivery: 'En livraison',
  delivered: 'Livré',
  completed: 'Terminée',
  cancelled: 'Annulée',
  expired: 'Expirée',
};

const typeLabels: Record<string, string> = {
  delivery: 'Livraison',
  errand: 'Course',
  meal: 'Repas',
  package: 'Colis',
};

const typeIcons: Record<string, any> = {
  delivery: Package,
  errand: Navigation,
  meal: UtensilsIcon,
  package: BriefcaseIcon,
};

const statusOrder = ['accepted', 'picked_up', 'en_route_delivery', 'delivered', 'completed'];

const statusColors: Record<string, string> = {
  requested: 'bg-gray-100 text-gray-600',
  searching: 'bg-amber-100 text-amber-600',
  assigned: 'bg-blue-100 text-blue-600',
  accepted: 'bg-blue-100 text-blue-600',
  en_route_pickup: 'bg-purple-100 text-purple-600',
  picked_up: 'bg-indigo-100 text-indigo-600',
  en_route_delivery: 'bg-purple-100 text-purple-600',
  delivered: 'bg-green-100 text-green-600',
  completed: 'bg-green-100 text-green-600',
  cancelled: 'bg-red-100 text-red-600',
  expired: 'bg-gray-100 text-gray-600',
};

export default function RideTracking() {
  const { rideId } = useParams<{ rideId: string }>();
  const { currentRide, getRide } = useRide();
  const [ride, setRide] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const TypeIcon = ride ? (typeIcons[ride.type] || Package) : Package;

  useEffect(() => {
    if (!rideId) return;
    
    const loadRide = async () => {
      setLoading(true);
      setError('');
      try {
        const data = currentRide && currentRide.id === rideId ? currentRide : await getRide(rideId!);
        setRide(data);
      } catch (err) {
        setError('Erreur lors du chargement');
      } finally {
        setLoading(false);
      }
    };
    
    loadRide();
  }, [rideId, currentRide, getRide]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="loading-spinner-lg mx-auto mb-4 text-primary" />
          <p className="text-text-secondary">Chargement de la course...</p>
        </div>
      </div>
    );
  }

  if (error || !ride) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-background">
        <div className="card text-center max-w-md">
          <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
            <X size={32} className="text-red-500" />
          </div>
          <h2 className="text-xl font-semibold text-text mb-2">Course introuvable</h2>
          <p className="text-text-secondary mb-6">{error || 'Cette course n\'existe pas'}</p>
          <Link to="/" className="btn btn-primary">Retour à l'accueil</Link>
        </div>
      </div>
    );
  }

  const currentStatusIndex = statusOrder.indexOf(ride.status);
  const isCompleted = ['completed', 'delivered', 'cancelled'].includes(ride.status);

  return (
    <div className="min-h-screen bg-background">
      <div className="container pt-4 pb-28">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <Link to="/" className="btn btn-ghost p-2" aria-label="Retour">
            <ArrowLeft size={24} />
          </Link>
          <h1 className="text-xl font-semibold text-text">Suivi de course</h1>
          <div className="w-10" />
        </div>

        {/* Ride Header Card */}
        <div className="card mb-5 animate-fade-in">
          <div className="flex items-center gap-4 p-4 bg-gradient-to-r from-primary/5 to-secondary/5 rounded-xl">
            <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center text-primary flex-shrink-0">
              <TypeIcon size={28} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <h2 className="font-bold text-lg text-text">{typeLabels[ride.type] || ride.type}</h2>
                <span className={`badge ${statusColors[ride.status] || 'bg-gray-100 text-gray-600'}`}>
                  {statusLabels[ride.status] || ride.status}
                </span>
              </div>
              <p className="text-sm text-text-secondary">
                {ride.distanceKm?.toFixed(1)} km • {ride.estimatedDurationMin} min • {ride.priceFcfa.toLocaleString()} FCFA
              </p>
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold text-text">{ride.priceFcfa.toLocaleString()} FCFA</p>
              <p className="text-xs text-text-secondary">Commission: {(ride.priceFcfa * 0.15).toFixed(0)} FCFA</p>
            </div>
          </div>

          <div className="space-y-4 mt-4">
            <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-xl">
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                <MapPin size={22} className="text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs text-text-secondary uppercase tracking-wider">Départ</p>
                <p className="font-medium text-text truncate">{ride.pickupAddress}</p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-xl">
              <div className="w-12 h-12 rounded-xl bg-secondary/10 flex items-center justify-center flex-shrink-0">
                <MapPin size={22} className="text-secondary" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs text-text-secondary">Destination</p>
                <p className="font-medium text-text truncate">{ride.dropoffAddress}</p>
              </div>
            </div>
            {ride.notes && (
              <div className="flex items-start gap-3 p-4 bg-amber-50 rounded-xl border border-amber-100">
                <MessageSquare size={22} className="text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-xs text-amber-700 font-medium">Note du client</p>
                  <p className="text-text">{ride.notes}</p>
                </div>
              </div>
            )}
          </div>

          <div className="flex items-center justify-between pt-4 border-t border-border">
            <span className={`badge badge-lg ${statusColors[ride.status] || 'bg-gray-100 text-gray-600'}`}>
              {statusLabels[ride.status] || ride.status}
            </span>
            <div className="text-right">
              <p className="text-xs text-text-secondary">Livreur</p>
              <p className="font-medium text-text">{ride.agent?.fullName || 'En attente...'}</p>
            </div>
          </div>
        </div>

        {/* Progress Timeline */}
        <div className="card animate-slide-up">
          <h3 className="font-semibold text-text mb-5">Progression de la livraison</h3>
          <div className="space-y-5">
            {[
              { key: 'accepted', label: 'Course acceptée', desc: 'Un livreur a pris votre course', icon: CheckCircle },
              { key: 'picked_up', label: 'Colis récupéré', desc: 'Le livreur a récupéré votre colis', icon: Package },
              { key: 'delivered', label: 'Livré', desc: 'Colis remis au destinataire', icon: Star },
            ].map((step, i) => {
              const isActive = statusOrder.indexOf(ride.status) >= i;
              const isCurrent = ride.status === step.key;
              const isDone = statusOrder.indexOf(ride.status) > i;
              
              return (
                <div key={step.key} className="flex items-start gap-4">
                  <div className="relative flex-shrink-0">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 ${
                      isDone ? 'bg-primary text-white shadow-lg' : isActive ? 'bg-primary/10 text-primary border-2 border-primary' : 'bg-gray-200 text-gray-400'
                    }`}>
                      {isDone ? <CheckCircle size={18} /> : <step.icon size={18} />}
                    </div>
                    <div className={`absolute left-5 top-10 bottom-0 w-0.5 ${i < 2 ? (isDone ? 'bg-primary' : 'bg-gray-200') : 'hidden'}`} />
                  </div>
                  <div className="flex-1 min-w-0 pt-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`font-medium text-text ${isActive ? 'text-primary' : ''}`}>
                        {step.label}
                      </span>
                      {isCurrent && <span className="badge badge-primary badge-sm animate-pulse">En cours</span>}
                      {isDone && <span className="badge badge-success badge-sm">Terminé</span>}
                    </div>
                    <div className="text-xs text-text-secondary">
                      {ride[step.key + 'At'] 
                        ? format(new Date(ride[step.key + 'At']), 'HH:mm - dd/MM/yyyy')
                        : isActive ? 'En cours...' : 'En attente'}
                    </div>
                  </div>
                </div>
              );
            })}
            
            {/* Agent info if assigned */}
            {ride.agent && (
              <div className="mt-6 pt-5 border-t border-border">
                <div className="flex items-center gap-4 p-4 bg-primary/5 rounded-xl">
                  <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center text-primary">
                    <Shield size={24} />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-text">Votre livreur</p>
                    <p className="text-text-secondary">{ride.agent.fullName}</p>
                    <div className="flex items-center gap-4 mt-2 text-sm text-text-secondary">
                      <span className="flex items-center gap-1"><Star size={14} /> {ride.agent.rating?.toFixed(1) || '5.0'}</span>
                      <span className="flex items-center gap-1"><Clock size={14} /> {ride.agent.totalRides || 0} courses</span>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-text-secondary">Joignable</p>
                    <p className="font-mono text-text">{ride.agent.phone}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}